import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, RefreshCw, Trash2, Upload } from "lucide-react";
import { useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { ApiError } from "@/api/client";
import {
  deleteKnowledgeFile,
  listKnowledgeFiles,
  replaceKnowledgeFile,
  uploadKnowledgeFile,
} from "@/api/knowledge";
import { EmptyState } from "@/components/EmptyState";
import { Spinner } from "@/components/Spinner";
import { formatDateTime } from "@/lib/formatDate";
import { AdminHeader } from "@/pages/admin/components/AdminHeader";

const ACCEPT = ".pdf,.docx,.txt,.xlsx,.xls,.csv";

export function TabKnowledgePage() {
  const { id: tabId } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const replaceRef = useRef<HTMLInputElement>(null);
  const replaceTargetRef = useRef<{ id: number; filename: string; pointCount: number } | null>(null);

  const list = useQuery({
    queryKey: ["knowledge", tabId],
    queryFn: () => listKnowledgeFiles(tabId!),
    enabled: !!tabId,
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadKnowledgeFile(tabId!, file),
    onSuccess: (r) => {
      toast[r.created ? "success" : "info"](
        r.created ? `Uploaded and indexed: ${r.file.filename}` : `Already indexed: ${r.file.filename}`,
      );
      void qc.invalidateQueries({ queryKey: ["knowledge", tabId] });
      void qc.invalidateQueries({ queryKey: ["tabs"] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Upload failed."),
  });

  const remove = useMutation({
    mutationFn: (fileId: number) => deleteKnowledgeFile(tabId!, fileId),
    onSuccess: () => {
      toast.success("File removed and vectors deleted.");
      void qc.invalidateQueries({ queryKey: ["knowledge", tabId] });
      void qc.invalidateQueries({ queryKey: ["tabs"] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Delete failed."),
  });

  const replace = useMutation({
    mutationFn: ({ fileId, file }: { fileId: number; file: File }) =>
      replaceKnowledgeFile(tabId!, fileId, file),
    onSuccess: (r) => {
      toast.success(`Replaced with: ${r.file.filename}`);
      void qc.invalidateQueries({ queryKey: ["knowledge", tabId] });
      void qc.invalidateQueries({ queryKey: ["tabs"] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Replace failed."),
  });

  return (
    <div className="min-h-screen flex flex-col">
      <AdminHeader title="Knowledge base for this tab" backTo="/admin/tabs" />
      <main className="w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1 flex flex-col gap-4">
        <div className="alert-info text-xs">
          Documents you upload here are chunked, embedded, and added to this tab's private Qdrant
          collection. They influence the AI response for grievances on this exam only. Accepted:
          <code> pdf, docx, txt, xlsx, xls, csv</code>. Duplicate uploads (same sha256) are ignored.
        </div>

        <div className="card p-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">Add a knowledge document</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Up to 200 MB per file.
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            className="btn-primary"
            onClick={() => fileRef.current?.click()}
            disabled={upload.isPending}
          >
            {upload.isPending ? <Spinner label="Indexing…" /> : <><Upload size={14} /> Upload</>}
          </button>
        </div>

        <input
          ref={replaceRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            const target = replaceTargetRef.current;
            replaceTargetRef.current = null;
            const picked = e.target.files?.[0];
            e.target.value = "";
            if (!target || !picked) return;
            const ok = window.confirm(
              `Replace "${target.filename}" with "${picked.name}"? ` +
                `Its ${target.pointCount} vectors will be removed and rebuilt from the new file.`,
            );
            if (!ok) return;
            replace.mutate({ fileId: target.id, file: picked });
          }}
        />

        {list.isLoading ? (
          <div className="card p-12 flex justify-center">
            <Spinner label="Loading files…" />
          </div>
        ) : list.isError ? (
          <div className="alert-danger">{(list.error as Error)?.message}</div>
        ) : list.data && list.data.items.length > 0 ? (
          <ul className="card divide-y divide-canvas-border dark:divide-surface-border">
            {list.data.items.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <FileText size={14} /> {f.filename}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate">
                    {(f.size_bytes / 1024).toFixed(1)} KB · {f.point_count} vectors ·
                    uploaded {formatDateTime(f.uploaded_at)}
                    {f.indexed_at ? ` · indexed ${formatDateTime(f.indexed_at)}` : " · not indexed"}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    className="btn-secondary text-xs"
                    disabled={
                      (replace.isPending && replace.variables?.fileId === f.id) ||
                      remove.isPending
                    }
                    onClick={() => {
                      replaceTargetRef.current = {
                        id: f.id,
                        filename: f.filename,
                        pointCount: f.point_count,
                      };
                      replaceRef.current?.click();
                    }}
                  >
                    {replace.isPending && replace.variables?.fileId === f.id ? (
                      <Spinner label="Replacing…" />
                    ) : (
                      <><RefreshCw size={12} /> Replace</>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn-danger text-xs"
                    disabled={replace.isPending && replace.variables?.fileId === f.id}
                    onClick={() =>
                      window.confirm(`Remove "${f.filename}" and its ${f.point_count} vectors?`) &&
                      remove.mutate(f.id)
                    }
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            title="No documents indexed yet."
            description="Upload PDFs, Word docs, spreadsheets, or plain-text notes about this exam's rules."
          />
        )}
      </main>
    </div>
  );
}
